import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CONTACT } from "@/constants/testIds";
import { ArrowUpRight, Loader2, Check, MessageCircle } from "lucide-react";

const WHATSAPP_NUMBER = "+971565674843";
const WHATSAPP_LINK = "https://wa.me/971565674843";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Landing Page",
  "Full Website",
  "Web App",
  "Booking Platform",
  "AI Feature",
  "Something else",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    project_type: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Message sent — I'll be in touch shortly.");
      setForm({ name: "", email: "", project_type: "", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please try WhatsApp instead: +971 56 567 4843.";
      toast.error(typeof detail === "string" ? detail : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={CONTACT.section}
      className="relative py-24 lg:py-40 border-t border-white/10 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.10] pointer-events-none"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1652120268427-9ba66e870467?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMHNoYXBlcyUyMDNkJTIwYmxhY2t8ZW58MHx8fHwxNzgzOTUxMjgzfDA&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left */}
          <div className="lg:col-span-5">
            <div className="font-mono-tag text-xs text-[#E2FF31] mb-6">
              // 004 — Contact
            </div>
            <h2 className="font-display font-medium text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mb-8">
              Have an idea?
              <br />
              Let&apos;s <span className="italic font-light text-[#E2FF31]">
                make it real.
              </span>
            </h2>
            <p className="text-white/65 text-base leading-relaxed mb-8 max-w-md">
              Tell me a little about the project. I read every message and
              respond within one business day — usually much faster.
            </p>

            {/* Trust chips */}
            <div className="flex flex-wrap gap-2 mb-10">
              {[
                "Free intro call",
                "Fixed quote",
                "Under 2 weeks",
                "1yr support",
              ].map((chip) => (
                <span
                  key={chip}
                  className="px-3 py-1.5 rounded-full border border-white/15 font-mono-tag text-[10px] text-white/70"
                >
                  {chip}
                </span>
              ))}
            </div>

            <a
              data-testid="contact-whatsapp-primary"
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 font-display text-2xl sm:text-3xl font-medium hover:text-[#25D366] transition-colors group"
            >
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#25D366]/10 border border-[#25D366]/30 group-hover:bg-[#25D366]/20 transition-colors">
                <MessageCircle size={18} className="text-[#25D366]" />
              </span>
              {WHATSAPP_NUMBER}
              <ArrowUpRight size={20} />
            </a>

            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="font-mono-tag text-[10px] text-white/40 mb-2">
                // Prefer the form? Fill it out on the right.
              </div>
              <p className="text-white/60 text-sm max-w-md">
                Every enquiry is read by the founder personally — no ticket
                queues, no auto-replies.
              </p>
            </div>

            {/* Reply time signal */}
            <div className="mt-8 inline-flex items-center gap-3 px-4 py-3 rounded-full bg-[#10B981]/10 border border-[#10B981]/30 text-[#10B981] font-mono-tag text-[10px]">
              <span className="w-2 h-2 rounded-full bg-[#10B981] pulse-dot" />
              Currently replying within 24 hours
            </div>
          </div>

          {/* Right — Form */}
          <form
            data-testid={CONTACT.form}
            onSubmit={submit}
            className="lg:col-span-7 space-y-8"
          >
            <div>
              <label
                htmlFor="c-name"
                className="font-mono-tag text-[10px] text-white/40 block mb-2"
              >
                // Your Name
              </label>
              <input
                data-testid={CONTACT.inputName}
                id="c-name"
                name="name"
                value={form.name}
                onChange={onChange}
                placeholder="Jane Doe"
                autoComplete="name"
                required
                className="w-full bg-transparent border-b border-white/20 focus:border-[#E2FF31] px-0 py-3 font-display text-2xl sm:text-3xl placeholder:text-white/25 focus:outline-none transition-colors"
              />
            </div>

            <div>
              <label
                htmlFor="c-email"
                className="font-mono-tag text-[10px] text-white/40 block mb-2"
              >
                // Email
              </label>
              <input
                data-testid={CONTACT.inputEmail}
                id="c-email"
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                placeholder="jane@company.com"
                autoComplete="email"
                required
                className="w-full bg-transparent border-b border-white/20 focus:border-[#E2FF31] px-0 py-3 font-display text-2xl sm:text-3xl placeholder:text-white/25 focus:outline-none transition-colors"
              />
            </div>

            <div>
              <label className="font-mono-tag text-[10px] text-white/40 block mb-3">
                // Project Type
              </label>
              <div className="flex flex-wrap gap-2">
                {PROJECT_TYPES.map((t) => {
                  const active = form.project_type === t;
                  return (
                    <button
                      key={t}
                      type="button"
                      data-testid={`${CONTACT.inputProjectType}-${t
                        .toLowerCase()
                        .replace(/\s+/g, "-")}`}
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          project_type: active ? "" : t,
                        }))
                      }
                      className={`px-4 py-2 rounded-full border font-mono-tag text-[10px] transition-all ${
                        active
                          ? "bg-[#E2FF31] text-black border-[#E2FF31]"
                          : "border-white/20 text-white/70 hover:border-[#E2FF31] hover:text-[#E2FF31]"
                      }`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label
                htmlFor="c-msg"
                className="font-mono-tag text-[10px] text-white/40 block mb-2"
              >
                // Message
              </label>
              <textarea
                data-testid={CONTACT.inputMessage}
                id="c-msg"
                name="message"
                value={form.message}
                onChange={onChange}
                placeholder="Tell me about your project, goals and timeline..."
                rows={4}
                required
                className="w-full bg-transparent border-b border-white/20 focus:border-[#E2FF31] px-0 py-3 font-display text-xl sm:text-2xl placeholder:text-white/25 focus:outline-none transition-colors resize-none"
              />
            </div>

            <div className="pt-2 flex flex-wrap items-center justify-between gap-4">
              <div className="text-white/40 text-xs">
                By submitting you agree to be contacted about this enquiry.
              </div>
              <button
                data-testid={CONTACT.submit}
                type="submit"
                disabled={submitting || done}
                className="btn-primary disabled:opacity-70"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Sending
                  </>
                ) : done ? (
                  <span
                    data-testid={CONTACT.success}
                    className="inline-flex items-center gap-2"
                  >
                    <Check size={16} /> Message received
                  </span>
                ) : (
                  <>
                    Send message <span className="arrow">→</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
